import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Mail, ArrowRight } from "lucide-react";

const JUSTICE = "https://images.unsplash.com/photo-1768839722927-df0ef3188f6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG1vZGVybiUyMGxhdyUyMHNjYWxlcyUyMGp1c3RpY2UlMjBtaW5pbWFsaXNtfGVufDB8fHx8MTc4NjY5MTc3Nnww&ixlib=rb-4.1.0&q=85";
const GOOGLE_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim();

export default function Login() {
  const navigate = useNavigate();
  const { user, login, register, googleLogin } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const googleRef = useRef(null);

  useEffect(() => {
    if (user) navigate(user.role === "owner" ? "/admin" : "/app", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const render = () => {
      if (!window.google?.accounts?.id || !googleRef.current) return;
      googleRef.current.innerHTML = "";
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async ({ credential }) => {
          if (!credential) return;
          setLoading(true);
          try {
            const u = await googleLogin(credential);
            toast.success(`Welcome${u.name ? ", " + u.name : ""}`);
            navigate(u.role === "owner" ? "/admin" : "/app", { replace: true });
          } catch (err) {
            toast.error(err.response?.data?.detail || "Google authentication failed");
          } finally {
            setLoading(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleRef.current, {
        theme: "outline",
        size: "large",
        width: 350,
        text: "continue_with",
        shape: "rectangular",
      });
    };

    if (window.google?.accounts?.id) {
      render();
      return;
    }

    const existing = document.querySelector('script[data-hkl-google-identity="true"]');
    if (existing) {
      existing.addEventListener("load", render, { once: true });
      return () => existing.removeEventListener("load", render);
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.dataset.hklGoogleIdentity = "true";
    script.addEventListener("load", render, { once: true });
    document.head.appendChild(script);
    return () => script.removeEventListener("load", render);
  }, [googleLogin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = mode === "login" ? await login(email, password) : await register(name, email, password);
      toast.success(`Welcome${u.name ? ", " + u.name : ""}`);
      navigate(u.role === "owner" ? "/admin" : "/app", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block">
        <img src={JUSTICE} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Brand />
          <div>
            <h2 className="font-serif-display text-4xl font-semibold text-white leading-tight max-w-md">Research beside Leego. Keep control of the work.</h2>
            <p className="text-slate-300 mt-4 max-w-md text-sm leading-relaxed">HardKnockLegal combines grounded legal research, official sources, matter tools, and a persistent AI copilot without tying the platform to one model vendor.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm animate-rise">
          <div className="lg:hidden mb-8"><Brand /></div>
          <h1 className="font-serif-display text-3xl font-semibold tracking-tight">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-muted-foreground text-sm mt-2 mb-8">{mode === "login" ? "Sign in to continue your legal research." : "Start with free research access."}</p>

          {GOOGLE_CLIENT_ID && (
            <>
              <div ref={googleRef} className="min-h-[44px] flex justify-center" data-testid="google-login-container" />
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-border" />
                <span className="overline text-muted-foreground">or email</span>
                <div className="flex-1 h-px bg-border" />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="overline text-muted-foreground">Full Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="name-input" className="mt-1.5 rounded-none bg-secondary border-border focus-visible:ring-primary/50" placeholder="Jane Counsel" />
              </div>
            )}
            <div>
              <Label className="overline text-muted-foreground">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" className="mt-1.5 rounded-none bg-secondary border-border focus-visible:ring-primary/50" placeholder="you@example.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="overline text-muted-foreground">Password</Label>
                {mode === "login" && (
                  <Link to="/reset-password" data-testid="forgot-password-link" className="text-xs text-primary hover:underline underline-offset-4">Forgot password?</Link>
                )}
              </div>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="password-input" className="mt-1.5 rounded-none bg-secondary border-border focus-visible:ring-primary/50" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="submit-auth-btn" className="w-full h-11 rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" />{mode === "login" ? "Sign In" : "Create Account"}<ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="toggle-auth-mode" className="text-primary hover:underline underline-offset-4">{mode === "login" ? "Create an account" : "Sign in"}</button>
          </p>
        </div>
      </div>
    </div>
  );
}
